import { Settings2, X } from "lucide-react";
import { useId } from "react";
import { Link } from "react-router-dom";

import { useNavContext } from "@/app/auth/useNavContext";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaReveal } from "@/shared/components/motion/ColocaReveal";
import useTranslations from "@/shared/lang/useTranslations";

import { availableDashboardAlertTypes } from "./dashboardAlertAvailability";
import { DASHBOARD_ALERT_GROUPS } from "./dashboardAlertCatalog";
import {
  DASHBOARD_ALERT_GROUP_ICON,
  DASHBOARD_ALERT_GROUP_SOUND_DESCRIPTION,
  DASHBOARD_ALERT_GROUP_TITLE,
} from "./dashboardAlertDisplay";
import { DashboardAlertGroupSoundButtons } from "./DashboardAlertGroupSound";
import { useDashboardAlertIntro } from "./dashboardAlertIntro";

/** Settings → Avisos en el dashboard; the section id is the page's scroll anchor. */
const ALERT_SETTINGS_PATH = "/app/settings?section=avisos";

/** Three bars that rise and fall like a level meter: the card is about sound before a word is read. */
function SoundBars() {
  return (
    <span aria-hidden="true" className="flex h-4 items-end gap-[3px]">
      {[0, 180, 360].map((delay) => (
        <span
          key={delay}
          className="w-[3px] origin-bottom animate-alert-bars rounded-full bg-current motion-reduce:animate-none"
          style={{ animationDelay: `${delay}ms`, height: "100%" }}
        />
      ))}
    </span>
  );
}

/**
 * The card that comes with the first corner alert of a browser (`dashboardAlertIntro`): what the alerts are,
 * how each section the person can read sounds (with a button to hear it), and the way to Settings to choose
 * which ones sound, which only show and which are off. Floats bottom right (the toasts own the top), full
 * width at the bottom of a phone. Not modal: it never blocks the screen the alert arrived on.
 */
export function DashboardAlertsIntro() {
  const { t } = useTranslations("dashboardAlerts");
  const navContext = useNavContext();
  const { open, dismiss } = useDashboardAlertIntro();
  const titleId = useId();

  if (!open) return null;

  // Only the sections the person gets alerts from: the pages the sidebar shows them.
  const available = availableDashboardAlertTypes(navContext, { brebKeys: null });
  const groups = DASHBOARD_ALERT_GROUPS.filter((group) =>
    group.types.some((type) => available.includes(type)),
  );

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      // Esc closes it from inside only: pressed anywhere else it belongs to whatever else is open.
      onKeyDown={(event) => {
        if (event.key === "Escape") dismiss();
      }}
      className="fixed inset-x-3 bottom-3 z-50 animate-in fade-in slide-in-from-bottom-6 duration-500 motion-reduce:animate-none sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-[380px]"
    >
      <div className="rounded-2xl border border-border bg-card p-4 text-card-foreground shadow-lg">
        <div className="flex items-start gap-3">
          <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-success/10 text-success">
            <SoundBars />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-[15px] font-semibold text-foreground">
              {t("intro_title")}
            </h2>
            <p className="mt-1 text-[12.5px] leading-relaxed text-muted-foreground">{t("intro_body")}</p>
          </div>
          <ColocaIconButton
            size="xs"
            variant="ghost"
            icon={X}
            aria-label={t("intro_close")}
            onClick={dismiss}
            className="-mt-1 -mr-1 text-muted-foreground"
          />
        </div>

        <ColocaReveal className="mt-3 space-y-1.5" delay={0.25}>
          {groups.map((group) => {
            const Icon = DASHBOARD_ALERT_GROUP_ICON[group.id];
            return (
              <div
                key={group.id}
                data-reveal
                className="flex items-start gap-2.5 rounded-xl border border-border bg-background/60 px-2.5 py-2"
              >
                <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground">
                  <Icon className="size-3.5" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[12.5px] font-medium text-foreground">
                    {t(DASHBOARD_ALERT_GROUP_TITLE[group.id])}
                  </p>
                  <p className="text-[11px] leading-snug text-muted-foreground">
                    {t(DASHBOARD_ALERT_GROUP_SOUND_DESCRIPTION[group.id])}
                  </p>
                  <DashboardAlertGroupSoundButtons group={group.id} className="mt-1.5" />
                </div>
              </div>
            );
          })}
        </ColocaReveal>

        <p className="mt-3 text-[12px] text-muted-foreground">{t("intro_footer")}</p>
        <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
          <ColocaButton size="sm" variant="ghost" onClick={dismiss}>
            {t("intro_dismiss")}
          </ColocaButton>
          <ColocaButton
            size="sm"
            variant="primary"
            icon={Settings2}
            render={<Link to={ALERT_SETTINGS_PATH} />}
            onClick={dismiss}
          >
            {t("intro_settings")}
          </ColocaButton>
        </div>
      </div>
    </section>
  );
}
